import { useEffect, useState } from 'react';
import { Copy, Trash2, ExternalLink, MousePointerClick, Check } from 'lucide-react';
import { supabase, MaskedLink } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface LinkListProps {
  refreshTrigger: number;
}

export function LinkList({ refreshTrigger }: LinkListProps) {
  const { user } = useAuth();
  const [links, setLinks] = useState<MaskedLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedShort, setCopiedShort] = useState<string | null>(null);
  const [copiedTarget, setCopiedTarget] = useState<string | null>(null);

  const fetchLinks = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('masked_links')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLinks(data || []);
    } catch (err) {
      console.error('Error fetching links:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, [user, refreshTrigger]);

  const copyShortLink = (shortCode: string) => {
    const url = `${window.location.origin}/${shortCode}`;
    navigator.clipboard.writeText(url);
    setCopiedShort(shortCode);
    setTimeout(() => setCopiedShort(null), 2000);
  };

  const copyTargetUrl = (id: string, targetUrl: string) => {
    navigator.clipboard.writeText(targetUrl);
    setCopiedTarget(id);
    setTimeout(() => setCopiedTarget(null), 2000);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const deleteLink = async (id: string) => {
    if (!confirm('Are you sure you want to delete this link?')) return;

    try {
      const { error } = await supabase
        .from('masked_links')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setLinks(links.filter((link) => link.id !== id));
    } catch (err) {
      console.error('Error deleting link:', err);
      alert('Failed to delete link');
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
        <div className="text-center text-slate-500">Loading links...</div>
      </div>
    );
  }

  if (links.length === 0) {
    return (
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
        <div className="text-center text-slate-500">
          No links created yet. Create your first link above!
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Link History</h2>
        <div className="bg-slate-800 px-4 py-2 rounded-lg">
          <span className="text-slate-300 text-sm font-medium">{links.length} Links</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="text-left text-slate-400 text-xs font-medium uppercase tracking-wider pb-3 px-2">Date</th>
              <th className="text-left text-slate-400 text-xs font-medium uppercase tracking-wider pb-3 px-2">Preview</th>
              <th className="text-left text-slate-400 text-xs font-medium uppercase tracking-wider pb-3 px-2">Title</th>
              <th className="text-left text-slate-400 text-xs font-medium uppercase tracking-wider pb-3 px-2">Short Link</th>
              <th className="text-left text-slate-400 text-xs font-medium uppercase tracking-wider pb-3 px-2">Target URL</th>
              <th className="text-center text-slate-400 text-xs font-medium uppercase tracking-wider pb-3 px-2">Clicks</th>
              <th className="text-center text-slate-400 text-xs font-medium uppercase tracking-wider pb-3 px-2">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {links.map((link) => (
              <tr key={link.id} className="hover:bg-slate-800/50 transition">
                <td className="py-4 px-2">
                  <div className="text-slate-400 text-sm whitespace-nowrap">
                    {formatDate(link.created_at)}
                  </div>
                </td>
                <td className="py-4 px-2">
                  {link.og_image ? (
                    <img
                      src={link.og_image}
                      alt={link.title}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-slate-800 rounded-lg flex items-center justify-center">
                      <ExternalLink className="w-6 h-6 text-slate-600" />
                    </div>
                  )}
                </td>
                <td className="py-4 px-2">
                  <div className="max-w-xs">
                    <div className="text-white font-medium text-sm truncate mb-1">
                      {link.title}
                    </div>
                    <div className="text-slate-500 text-xs line-clamp-2">
                      {link.description}
                    </div>
                  </div>
                </td>
                <td className="py-4 px-2">
                  <div className="flex items-center gap-2">
                    <div className="text-emerald-400 text-sm font-mono bg-slate-800 px-3 py-1 rounded">
                      /{link.short_code}
                    </div>
                    <button
                      onClick={() => copyShortLink(link.short_code)}
                      className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded transition"
                      title="Copy short link"
                    >
                      {copiedShort === link.short_code ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </td>
                <td className="py-4 px-2">
                  <div className="flex items-center gap-2 max-w-xs">
                    <div className="text-slate-400 text-xs truncate flex-1">
                      {link.target_url}
                    </div>
                    <button
                      onClick={() => copyTargetUrl(link.id, link.target_url)}
                      className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded transition flex-shrink-0"
                      title="Copy target URL"
                    >
                      {copiedTarget === link.id ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </td>
                <td className="py-4 px-2">
                  <div className="flex items-center justify-center gap-1 text-slate-300">
                    <MousePointerClick className="w-4 h-4" />
                    <span className="text-sm font-semibold">{link.clicks}</span>
                  </div>
                </td>
                <td className="py-4 px-2">
                  <div className="flex items-center justify-center">
                    <button
                      onClick={() => deleteLink(link.id)}
                      className="p-2 bg-red-600/10 hover:bg-red-600 text-red-500 hover:text-white rounded-lg transition"
                      title="Delete link"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
